import React from 'react';
import { Clock, BookOpen, Users, Rocket } from 'lucide-react';

export default function ProjectHistoryPage() {
  return (
    <div className="min-h-screen bg-[#F5F5DC] pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-[#064635] mb-4">
            From MACOS Consultancy to Digital Archive
          </h1>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-8"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            The story of how scholarly publications became a lasting digital legacy
          </p>
        </div>

        {/* Origins */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-[#064635] mb-6">The MACOS Consultancy Era</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            For decades, E.S.V. Tomori conducted groundbreaking research through MACOS Consultancy,
            producing a remarkable body of work that documented the political organization, traditional
            history, and cultural heritage of Yoruba kingdoms and Ibadan society. These publications
            became essential resources for understanding the complex social structures and historical
            development of the Yoruba people.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Each publication represented years of careful research, oral history collection, and
            scholarly analysis—covering topics from the dynamics of honor in violence to the emergence
            of the Oduduwa kingdom, from Ibadan's unique position in Yoruba traditional history to
            the foundations of traditional society.
          </p>
        </div>

        {/* Milestones */}
        <div className="space-y-6 mb-12">
          <h2 className="text-2xl font-semibold text-[#064635] mb-6">The Journey to Digital Preservation</h2>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start">
              <BookOpen className="h-8 w-8 text-[#D4AF37] mr-4 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-[#064635] mb-2">Recognition of Need</h3>
                <p className="text-gray-600">
                  As physical copies of MACOS publications aged and became increasingly difficult to access,
                  the need for preservation became urgent. These valuable research materials risked being
                  lost to time, limiting access for future scholars and students.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start">
              <Clock className="h-8 w-8 text-[#D4AF37] mr-4 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-[#064635] mb-2">The Digitization Initiative</h3>
                <p className="text-gray-600">
                  The Echoes of Ibadan project was launched to systematically digitize and preserve E.S.V.
                  Tomori's entire body of work. Each publication is carefully scanned, catalogued, and made
                  available through a modern web platform accessible to researchers worldwide.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-start">
              <Users className="h-8 w-8 text-[#D4AF37] mr-4 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-[#064635] mb-2">Building the Archive</h3>
                <p className="text-gray-600">
                  Today, Echoes of Ibadan serves as a comprehensive digital repository, organizing
                  publications by topic and making them freely available to anyone interested in Yoruba
                  history, from academic researchers to students and cultural enthusiasts.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Looking Ahead */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex items-center mb-6">
            <Rocket className="h-8 w-8 text-[#D4AF37] mr-4" />
            <h2 className="text-2xl font-semibold text-[#064635]">Looking Ahead</h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            The digitization work continues as we add more publications to the archive and enhance
            accessibility features. Future plans include translations to Yoruba language, improved
            search capabilities, and collaboration with academic institutions to ensure this research
            reaches those who need it most.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Echoes of Ibadan stands as a testament to the enduring value of scholarly research and the
            importance of preserving cultural knowledge. What began as MACOS Consultancy publications
            now lives on as an accessible digital archive for generations to come.
          </p>
        </div>
      </div>
    </div>
  );
}